import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaEye, FaTimesCircle, FaSearch, FaFilter } from "react-icons/fa";
import { useVerificationRequests, useVerifyOrganizationStatus } from "../../hooks/useAdmin.ts";
import Pagination from "../../components/Pagination.tsx";
import { Link } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";

interface VerificationRequest {
  id: string;
  organization: string;
  organization_name: string;
  requested_at: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  organization_id: number;
}

const VerificationRequests: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const [requests, setRequests] = useState<VerificationRequest[]>([]);
  const [popup, setPopup] = useState<{ type: "approve" | "reject"; request: VerificationRequest } | null>(null);

  const { data: requestsData, isLoading: isLoadingRequests, error: requestsError } = useVerificationRequests();
  const verifyMutation = useVerifyOrganizationStatus();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "PENDING" | "APPROVED" | "REJECTED">("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  useEffect(() => {
    if (requestsData) {
      // Filter and sort organization requests
      const orgRequests = ((requestsData as any)?.data || requestsData || [])
        .filter(
          (req: any) => 
            req.organization !== null && 
            req.organization_name !== null && 
            req.organization_name !== undefined
        )
        // Sort by requested_at in descending order (newest first)
        .sort((a: any, b: any) =>
          new Date(b.requested_at).getTime() - new Date(a.requested_at).getTime()
        );
      setRequests(orgRequests);
    }
  }, [requestsData]);

  const handleConfirm = async () => {
    if (popup) {
      const id = popup.request.organization;
      const is_verified = popup.type === "approve";

      try {
        await verifyMutation.mutateAsync({ id, status: is_verified }, {
          onSuccess: () => {
            setPopup(null);
          },
          onError: (err) => {
            console.log(err);
          }
        });
      } catch (err) {
        console.log(err);
      }
    }
  };

  // Helper to get status display text
  const getStatusDisplay = (status: string) => {
    if (!status) return "UNKNOWN";
    return status.charAt(0) + status.slice(1).toLowerCase();
  };

  // FIXED: Filter requests based on search and status - Now properly checks organization_name
  const filteredRequests = requests.filter(request => {
    // Check if organization_name exists and matches search
    const matchesSearch = searchTerm === "" || (
      request.organization_name && 
      request.organization_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const matchesStatus = statusFilter === "ALL" || request.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredRequests.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  // TanStack Table column definitions
  const columnHelper = createColumnHelper<VerificationRequest>();

  const columns: ColumnDef<VerificationRequest, any>[] = [
    columnHelper.accessor('organization_name', {
      header: 'Organization',
      cell: (info) => {
        const name = info.getValue();
        return (
          <div className="font-medium hover:text-primary-600 transition-colors">
            {name || 'Unknown Organization'}
          </div>
        );
      },
    }),
    columnHelper.accessor('requested_at', {
      header: 'Requested At',
      cell: (info) => {
        const date = info.getValue();
        return date ? (
          <div>
            {new Date(date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
            <span className="block text-xs" style={{ color: currentTheme.textSecondary, marginTop: '4px' }}>
              {new Date(date).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        ) : 'N/A';
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => {
        const status = info.getValue();
        return (
          <span
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
            style={{
              backgroundColor: status === "APPROVED" ? currentTheme.success :
                status === "REJECTED" ? currentTheme.error : currentTheme.warning,
              color: currentTheme.inverseText
            }}
          >
            {getStatusDisplay(status)}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: (props) => {
        const request = props.row.original;
        return (
          <div className="text-right text-sm font-medium flex justify-end items-center space-x-3">
            <Link
              to={`/dashboard/viewdocs/${request.organization_id}`}
              className="transition-all p-2 rounded-full hover:scale-110 hover:shadow-lg"
              style={{ 
                color: currentTheme.text,
                backgroundColor: `${currentTheme.primary}10`
              }}
              title="View Documents"
            >
              <FaEye className="w-5 h-5" />
            </Link>
            <button
              className="transition-all p-2 rounded-full hover:scale-110 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                color: request.status === "APPROVED" ? currentTheme.textSecondary : currentTheme.success,
                backgroundColor: request.status === "APPROVED" ? 'transparent' : `${currentTheme.success}10`
              }}
              onClick={() =>
                request.status !== "APPROVED" &&
                setPopup({ type: "approve", request })
              }
              disabled={request.status === "APPROVED"}
              title="Approve Request"
            >
              <FaCheckCircle className="w-5 h-5" />
            </button>
            <button
              className="transition-all p-2 rounded-full hover:scale-110 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                color: request.status === "REJECTED" ? currentTheme.textSecondary : currentTheme.error,
                backgroundColor: request.status === "REJECTED" ? 'transparent' : `${currentTheme.error}10`
              }}
              onClick={() =>
                request.status !== "REJECTED" &&
                setPopup({ type: "reject", request })
              }
              disabled={request.status === "REJECTED"}
              title="Reject Request"
            >
              <FaTimesCircle className="w-5 h-5" />
            </button>
          </div>
        );
      },
    }),
  ];

  // TanStack Table instance
  const table = useReactTable({
    data: filteredRequests,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  // Get sorted rows, then paginate manually
  const sortedRows = table.getRowModel().rows;
  const paginatedRows = sortedRows.slice(startIndex, endIndex);

  // Calculate stats
  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === "PENDING").length,
    approved: requests.filter(r => r.status === "APPROVED").length,
    rejected: requests.filter(r => r.status === "REJECTED").length,
  };

  return (
    <div className="min-h-screen p-4 md:p-6" style={{ backgroundColor: currentTheme.background }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Verification Requests
          </h1>
          <p className="text-sm" style={{ color: currentTheme.textSecondary }}>
            Manage organization verification status
          </p>
        </div>

        {/* Stats Cards */}
        {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard 
            title="Total Requests"
            value={stats.total}
            icon={<FaFilter />}
            color="from-blue-500 to-blue-600"
            theme={currentTheme}
          />
          <StatCard 
            title="Pending"
            value={stats.pending}
            icon={<FaSearch />}
            color="from-yellow-500 to-yellow-600"
            theme={currentTheme}
          />
          <StatCard 
            title="Approved"
            value={stats.approved}
            icon={<FaCheckCircle />}
            color="from-green-500 to-green-600"
            theme={currentTheme}
          />
          <StatCard 
            title="Rejected"
            value={stats.rejected}
            icon={<FaTimesCircle />}
            color="from-red-500 to-red-600"
            theme={currentTheme}
          />
        </div> */}

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: currentTheme.textSecondary }} />
            <input
              type="text"
              placeholder="Search by organization name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none transition-all duration-300 focus:ring-2"
              style={{ 
                backgroundColor: currentTheme.surface, 
                color: currentTheme.text, 
                border: `1px solid ${currentTheme.border}`,
                boxShadow: `0 4px 12px ${currentTheme.primary}10`
              }}
            />
          </div>
          
          <div className="relative min-w-[200px]">
            <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: currentTheme.textSecondary }} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none transition-all duration-300 appearance-none cursor-pointer"
              style={{ 
                backgroundColor: currentTheme.surface, 
                color: currentTheme.text, 
                border: `1px solid ${currentTheme.border}`,
                boxShadow: `0 4px 12px ${currentTheme.primary}10`
              }}
            >
              <option value="ALL">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl shadow-2xl overflow-hidden transition-all hover:shadow-3xl"
             style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
          <div className="overflow-x-auto overflow-y-hidden">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id} style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.primary}DD)` }}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                        className={`px-6 py-4 text-left font-semibold text-sm ${header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-white/10' : ''}`}
                        style={{ color: currentTheme.inverseText }}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="flex flex-col text-[10px] leading-none">
                              <span style={{ opacity: header.column.getIsSorted() === 'asc' ? 1 : 0.5 }}>▲</span>
                              <span style={{ opacity: header.column.getIsSorted() === 'desc' ? 1 : 0.5 }}>▼</span>
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row, index) => (
                    <tr
                      key={row.id}
                      className="group transition-all duration-300 hover:bg-opacity-80"
                      style={{ 
                        backgroundColor: index % 2 === 0 ? currentTheme.surface : currentTheme.card,
                        borderBottom: `1px solid ${currentTheme.border}`
                      }}
                      onMouseEnter={() => setHoveredRow(row.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-6 py-4" style={{ color: currentTheme.text }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="p-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                          <FaSearch className="text-3xl" style={{ color: currentTheme.primary }} />
                        </div>
                        <h3 className="text-xl font-semibold" style={{ color: currentTheme.text }}>
                          No Verification Requests Found
                        </h3>
                        <p style={{ color: currentTheme.textSecondary }}>
                          {searchTerm || statusFilter !== "ALL" 
                            ? "Try adjusting your search or filter criteria" 
                            : "All verification requests have been processed"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredRequests.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: currentTheme.border }}>
              <div className="flex items-center gap-4">
                <select
                  value={rowsPerPage}
                  onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                  className="px-3 py-2 rounded-xl border focus:outline-none focus:ring-2 transition-all cursor-pointer"
                  style={{
                    backgroundColor: currentTheme.surface,
                    color: currentTheme.text,
                    borderColor: currentTheme.border
                  }}
                >
                  {[10, 25, 50, 100].map(value => (
                    <option key={value} value={value}>{value} per page</option>
                  ))}
                </select>
                <span className="text-sm" style={{ color: currentTheme.textSecondary }}>
                  Showing <span className="font-semibold" style={{ color: currentTheme.text }}>{startIndex + 1}</span> to{' '}
                  <span className="font-semibold" style={{ color: currentTheme.text }}>{Math.min(endIndex, filteredRequests.length)}</span> of{' '}
                  <span className="font-semibold" style={{ color: currentTheme.text }}>{filteredRequests.length}</span> requests
                </span>
              </div>
              <Pagination 
                currentPage={currentPage} 
                totalPages={totalPages} 
                onPageChange={handlePageChange} 
              />
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Popup */}
      {popup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
          <div className="rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in-0 zoom-in-95 duration-300"
               style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
            
            <h3 className="text-2xl font-bold mb-4" style={{ color: currentTheme.text }}>
              {popup.type === "approve" ? "Approve Verification" : "Reject Verification"}
            </h3>
            
            <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: `${currentTheme.primary}10` }}>
              <div className="font-semibold text-lg mb-1" style={{ color: currentTheme.text }}>
                {popup.request.organization_name || 'Unknown Organization'}
              </div>
              <div className="text-sm" style={{ color: currentTheme.textSecondary }}>
                Requested: {popup.request.requested_at 
                  ? new Date(popup.request.requested_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'N/A'}
              </div>
            </div>
            
            <p className="mb-6" style={{ color: currentTheme.textSecondary }}>
              Are you sure you want to {popup.type === "approve" ? "approve" : "reject"} this verification request? 
              {popup.type === "reject" && " This action cannot be undone."}
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                className="px-6 py-2.5 rounded-xl font-medium transition-all duration-300 hover:scale-105"
                style={{
                  color: currentTheme.text,
                  backgroundColor: currentTheme.surface,
                  border: `1px solid ${currentTheme.border}`
                }}
                onClick={() => setPopup(null)}
              >
                Cancel
              </button>
              <button
                className="px-6 py-2.5 rounded-xl font-medium text-white transition-all duration-300 hover:scale-105 hover:shadow-lg"
                style={{
                  backgroundColor: popup.type === "approve" ? currentTheme.success : currentTheme.error
                }}
                onClick={handleConfirm}
              >
                {popup.type === "approve" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  theme: any;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, theme }) => (
  <div className="group/stat relative overflow-hidden rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl"
       style={{ 
         background: `linear-gradient(135deg, ${theme.surface}, ${theme.card})`,
         border: `1px solid ${theme.border}`
       }}>
    <div className={`absolute inset-0 bg-gradient-to-r ${color} opacity-0 group-hover/stat:opacity-5 transition-opacity duration-500`}></div>
    <div className="relative">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-3 rounded-xl bg-gradient-to-r ${color} bg-opacity-10`}>
          <div className="text-white">{icon}</div>
        </div>
        <span className="text-3xl font-bold" style={{ color: theme.text }}>{value}</span>
      </div>
      <p className="text-sm" style={{ color: theme.textSecondary }}>{title}</p>
    </div>
  </div>
);

export default VerificationRequests;